import {
  LOCAL_STORAGE_SERVICE,
  LOGGER,
  type LocalStorageService,
  type Logger,
} from "@packages/common";
import { Inject, Injectable } from "@webmotors/ioc";
import {
  GEOLOCATION_COOKIE_SERVICE,
  type GeolocationCookieService,
  LEAD_FORM_DATA_SERVICE,
  type LeadFormDataService,
} from "../cookies";
import type { User } from "../user";
import { ADOBE_SERVICE } from "./adobe.types";
import type { DataLayerDTO } from "./dtos";
import { UserTransformer } from "./transformers";

@Injectable(ADOBE_SERVICE)
export class AdobeService {
  private readonly logger: Logger;

  /**
   * Initializes a new instance of the `AdobeService` class.
   * @param storage - The local storage service for managing local storage operations.
   * @param leadFormData - The lead form data service for handling cookies.
   * @param loggerFactory - The logger factory for creating a logger instance.
   */
  constructor(
    @Inject(LOCAL_STORAGE_SERVICE)
    private readonly storage: LocalStorageService,
    @Inject(LEAD_FORM_DATA_SERVICE)
    private readonly leadFormData: LeadFormDataService,
    @Inject(GEOLOCATION_COOKIE_SERVICE)
    private readonly geolocation: GeolocationCookieService,
    @Inject(LOGGER) private readonly loggerFactory: Logger
  ) {
    this.logger = this.loggerFactory.create(ADOBE_SERVICE);
  }

  /**
   * Creates a data layer object by merging default values with input parameters.
   *
   * @param params - The custom data layer parameters.
   * @param user - The current user object, or `null` if not logged in.
   * @returns The merged data layer object.
   */
  createDataLayerObject({
    params,
    eventType,
    user,
  }: {
    params: DataLayerDTO;
    eventType: string;
    user: User | null;
  }) {
    const defaultValues = this.getDefaultDataLayer(user);

    const mergedDataLayer = {
      ...defaultValues,
      ...params,
      site: {
        ...defaultValues.site,
        ...params.site,
      },
      page: {
        ...defaultValues.page,
        ...params.page,
      },
    };
    this.logger.log(
      `[Create] data layer object for event "${eventType}": `,
      mergedDataLayer,
      JSON.stringify(mergedDataLayer)
    );

    return mergedDataLayer;
  }

  /**
   * Provides the default data layer values.
   *
   * @param user - The current user object, or `null` if not logged in.
   * @returns The default data layer object.
   */
  private getDefaultDataLayer(user: User | null) {
    return {
      site: {
        domain: "www.webmotors.com.br",
        country: "brasil",
        server: "web",
        tmsVersion: undefined,
        clientType: "pf",
        brandOrigin: "webmotors",
      },
      page: {
        url: undefined,
        trackingCode: {
          idcmp: undefined,
          idcmpint: undefined,
          icid: undefined,
          lkid: undefined,
        },
      },
      user: UserTransformer.transform(
        user,
        this.leadFormData,
        this.storage,
        this.geolocation
      ),
    };
  }
}
